import * as THREE from 'three';
import type { RegionAnchor } from '../pharmacology/types';

const MAGIC = 'HUBM';
const HEADER_BYTES = 24;

/**
 * Transform mapping MNI-style anatomical coordinates into the normalized mesh
 * space, derived from the fsaverage5 surface by tools/prepare_brain_mesh.py.
 *
 * The prep script recentres the surface and scales it so the anterior-posterior
 * extent spans 2.015 units. These constants reproduce that same mapping, so
 * region coordinates can be written as published anatomical coordinates and
 * land in the right place on the mesh.
 *
 * Caveat: fsaverage surface RAS is approximately MNI305, which differs from
 * MNI152 by a few millimetres. At this visualization's scale that offset is far
 * below the size of the region markers, but these are illustrative placements,
 * not stereotactic targets.
 */
const MNI_CENTER_RIGHT = 0.529;
const MNI_CENTER_ANTERIOR = -17.768;
const MNI_CENTER_SUPERIOR = 15.409;
const MNI_SCALE = 0.011591;

/**
 * Converts an MNI coordinate (x=right, y=anterior, z=superior, in mm) into the
 * mesh's normalized space (x=right, y=superior, z=anterior).
 */
export function mniToMeshSpace(
  right: number,
  anterior: number,
  superior: number,
): [number, number, number] {
  return [
    (right - MNI_CENTER_RIGHT) * MNI_SCALE,
    (superior - MNI_CENTER_SUPERIOR) * MNI_SCALE,
    (anterior - MNI_CENTER_ANTERIOR) * MNI_SCALE,
  ];
}

/**
 * Converts a length in millimetres into mesh units at the same scale the
 * cortical surface was normalized to. Lets the procedural cerebellum and brain
 * stem be specified by their real anatomical dimensions rather than by numbers
 * tuned by eye against whatever the cortex mesh happens to measure.
 */
export function mmToMeshUnits(mm: number): number {
  return mm * MNI_SCALE;
}

/**
 * Resolves a region anchor into scene coordinates, whichever space it was
 * authored in. Brain structures are anchored in MNI; structures below the head
 * use the body frame, which is simply MNI recentred on the brain centroid — so
 * the two agree by construction rather than needing to be kept in sync by hand.
 */
export function resolveAnchor(anchor: RegionAnchor): [number, number, number] {
  const [a, b, c] = anchor.coords;
  return anchor.space === 'mni' ? mniToMeshSpace(a, b, c) : [mmToMeshUnits(a), mmToMeshUnits(b), mmToMeshUnits(c)];
}

/**
 * Loads the prepared cortical surface mesh.
 *
 * Format is documented in tools/prepare_brain_mesh.py — a short header followed
 * by int16-quantized positions and uint16 triangle indices. Deliberately not
 * glTF: this is wireframe-only geometry with no materials, normals, UVs or
 * animation, so a bespoke 24-byte header beats pulling in GLTFLoader, and the
 * prep script is committed so the asset stays reproducible from its source.
 *
 * Throws on a malformed or truncated file so the caller can fall back to the
 * procedural geometry rather than rendering nothing.
 */
export async function loadCortexMesh(url: string): Promise<THREE.BufferGeometry> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`cortex mesh fetch failed: ${response.status} ${response.statusText}`);
  }

  const buffer = await response.arrayBuffer();
  if (buffer.byteLength < HEADER_BYTES) {
    throw new Error(`cortex mesh too small: ${buffer.byteLength} bytes`);
  }

  const magic = String.fromCharCode(...new Uint8Array(buffer, 0, 4));
  if (magic !== MAGIC) {
    throw new Error(`cortex mesh bad magic: expected ${MAGIC}, got ${JSON.stringify(magic)}`);
  }

  const header = new DataView(buffer);
  const version = header.getUint32(4, true);
  if (version !== 1) {
    throw new Error(`unsupported cortex mesh version: ${version}`);
  }

  const vertexCount = header.getUint32(8, true);
  const indexCount = header.getUint32(12, true);
  const quantScale = header.getFloat32(16, true);

  const positionBytes = vertexCount * 3 * 2;
  const indexBytes = indexCount * 2;
  const expected = HEADER_BYTES + positionBytes + indexBytes;
  if (buffer.byteLength < expected) {
    throw new Error(`cortex mesh truncated: expected ${expected} bytes, got ${buffer.byteLength}`);
  }

  // Copy rather than view: the quantized source is int16 and the offsets are not
  // guaranteed to satisfy Float32Array's alignment requirement.
  const quantized = new Int16Array(buffer, HEADER_BYTES, vertexCount * 3);
  const positions = new Float32Array(vertexCount * 3);
  for (let i = 0; i < positions.length; i++) {
    positions[i] = quantized[i] * quantScale;
  }

  const indices = new Uint16Array(indexCount);
  const indexOffset = HEADER_BYTES + positionBytes;
  for (let i = 0; i < indexCount; i++) {
    indices[i] = header.getUint16(indexOffset + i * 2, true);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.computeVertexNormals();

  return geometry;
}
